import { useCallback, useEffect, useRef, useState } from 'react'

import { type Code, ConnectError } from '@connectrpc/connect'
import { zodResolver } from '@hookform/resolvers/zod'
import type { TFunction } from 'i18next'
import type {
  Control,
  DefaultValues,
  FieldErrors,
  FieldValues,
  Path,
  Resolver,
  UseFormGetValues,
  UseFormReset,
  UseFormSetError,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import type { ZodType } from 'zod'

import { useSuccessToast } from '~/hooks/useSuccessToast'
import type { AppErrorInfoResolution } from '~/libs/appErrorInfo'
import { resolveFormErrorAlertMessage } from '~/libs/formErrors'
import { isNetworkError } from '~/libs/isNetworkError'
import { mapErrorToMessage } from '~/libs/mapConnectError'

/**
 * `onSubmit` がユーザーの明示的キャンセル (= 保存前の追加確認ダイアログで
 * 「キャンセル」を選んだ等) によって処理を中断したことを表すマーカー error。
 *
 * `handleSave` の catch はこの error を検出すると `setServerError` を呼ばず
 * 静かに return する (= reset/onClose も呼ばれずモーダルは開いたまま残る)。
 * 通常の技術的失敗 / サーバーエラーとは異なり「ユーザーが元に戻った」だけの
 * 経路なのでエラーメッセージ表示は不要 (AC-OMT-020 の名称重複警告ダイアログで
 * 「キャンセル」を選んだ場合など)。
 */
export class SilentCancelSubmitError extends Error {}

type UseCrudModalFormOptions<
  Input extends FieldValues,
  Output extends FieldValues,
  Confirmation,
> = {
  schema: ZodType
  defaultValues: DefaultValues<Input>
  onClose: () => void
  customErrorKeys?: Partial<Record<Code, string>>
  unknownErrorKey?: string
  /**
   * Resolves a structured `google.rpc.ErrorInfo` Connect error detail
   * (design note `docs/adr/structured-app-error-contract.md` decision D) to a
   * localized message + field-scoped/operation-scoped classification.
   * Callers pass `(err, t, values) => resolveXxxDetailedError(err, t,
   * values)`. When it returns a `'field'` resolution, `setError` is
   * called on that field instead of the top-level `serverError` alert.
   * `undefined` (no detail attached, or an unrecognized `reason`) falls
   * back to the existing `customErrorKeys` / `unknownErrorKey` code-based
   * message — this option never narrows what errors already displayed.
   *
   * `values` is the exact `Output` object `handleSave`'s `handleSubmit`
   * callback just submitted (occto-plan-master-reason-registry 設計書
   * 実装注意, N3 stale-closure guard): it is passed as a plain callback
   * parameter, never captured from outer render scope, so a reason
   * whose message interpolates a submitted field (例: 重複エラーの
   * `{{bgCode}}`) always reflects the value that was actually sent, not
   * a stale value from a previous render.
   */
  resolveDetailedError?: (
    err: ConnectError,
    t: TFunction,
    values: Output,
  ) => AppErrorInfoResolution | undefined
} & (
  | {
      onSubmit: (values: Output) => Promise<void>
      prepareSubmit?: never
      confirmationOf?: never
    }
  | {
      onSubmit?: never
      prepareSubmit: (values: Output) => {
        identity: Uint8Array
        send: (confirmed: boolean) => Promise<unknown>
      }
      confirmationOf: (error: unknown) => Confirmation | undefined
    }
)

type UseCrudModalFormReturn<
  Input extends FieldValues,
  Output extends FieldValues,
  Confirmation,
> = {
  confirmation: Confirmation | undefined
  confirm: () => void
  cancelConfirmation: () => void
  control: Control<Input, unknown, Output>
  errors: FieldErrors<Input>
  serverError: string | undefined
  handleClose: () => void
  handleSave: () => void
  setServerError: (message: string) => void
  reset: UseFormReset<Input>
  isSubmitting: boolean
  setValue: UseFormSetValue<Input>
  watch: UseFormWatch<Input>
  setError: UseFormSetError<Input>
  /**
   * form の現在値を同期取得する。 handleSave 直前の pre-check (例: 期間外 BG の
   * 検出) に使う (`watch()` は subscribe/re-render を伴うので副次的だが、
   * event handler 内では `getValues` が本命)。
   */
  getValues: UseFormGetValues<Input>
  /**
   * defaultValues から変更された入力があるか (= 未保存の変更)。
   * `CrudModalLayout` の `isDirty` に渡すと、 入力途中でモーダルを閉じようと
   * した時に離脱確認ダイアログが出る (`~/hooks/useUnsavedGuard`)。
   * Edit モーダルは `reset(fetched)` で defaultValues が差し替わるため、
   * 取得値の反映だけでは dirty にならない。
   */
  isDirty: boolean
}

export function useCrudModalForm<
  Input extends FieldValues,
  Output extends FieldValues = Input,
  Confirmation = never,
>(
  options: UseCrudModalFormOptions<Input, Output, Confirmation>,
): UseCrudModalFormReturn<Input, Output, Confirmation> {
  const {
    schema,
    defaultValues,
    onSubmit,
    prepareSubmit,
    confirmationOf,
    onClose,
    customErrorKeys,
    unknownErrorKey,
    resolveDetailedError,
  } = options
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()

  const {
    control,
    getValues,
    handleSubmit,
    reset,
    clearErrors,
    setError,
    setFocus,
    setValue,
    watch,
    // `isDirty` は RHF の**値ベース**判定 (現在値 vs defaultValues)。
    //
    // **[design 差異]** design の `js/unsaved-guard.js` は **event ベース**で、
    // 対象領域内で input/change が一度でも起きたら dirty を立て、 submit / reset
    // まで下ろさない (同ファイル L30-80)。 そちらに合わせると「1 文字打って
    // 消して元に戻した」 状態でも離脱確認が出る。
    //
    // 値ベースを採る理由: この確認ダイアログの目的は**失われる編集を守ること**で、
    // 値が初期値に戻っているなら守るべき編集は存在しない。 そこで確認を出すのは
    // ユーザーから見て理由の無い引き止めになる。 design 側が event ベースなのは
    // vanilla JS で値比較を持てない実装都合と見られ、 意図的な UX 判断とは考えにくい
    // (design 側に「戻しても警告を維持する」旨の記述は無い)。
    //
    // 2026-08-12 のレビュー指摘 (PR #5256) を受けての明示的な判断。 design 側を
    // 値ベースへ寄せるかはデザイナー確認に回す。
    formState: { errors, isSubmitting, isDirty },
  } = useForm<Input, unknown, Output>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(schema as any) as Resolver<Input, unknown, Output>,
    mode: 'onBlur',
    defaultValues,
  })

  const [confirmation, setConfirmation] = useState<Confirmation>()
  const revision = useRef(0)
  const generation = useRef(0)
  const mounted = useRef(true)
  const running = useRef(false)
  const pendingFocus = useRef<{ field: Path<Input>; attempt: number }>()
  const awaiting = useRef<{ identity: Uint8Array; revision: number }>()
  const cancelConfirmation = useCallback(() => {
    awaiting.current = undefined
    setConfirmation(undefined)
  }, [])
  useEffect(() => {
    mounted.current = true
    const subscription = watch(() => {
      revision.current += 1
      cancelConfirmation()
    })
    return () => {
      mounted.current = false
      generation.current += 1
      subscription.unsubscribe()
    }
  }, [watch, cancelConfirmation])

  // Fast failures can batch away the submitting render; consume after each DOM
  // commit so a field disabled during saving receives focus once it is enabled.
  useEffect(() => {
    if (isSubmitting) return
    const requested = pendingFocus.current
    pendingFocus.current = undefined
    if (
      requested &&
      mounted.current &&
      requested.attempt === generation.current
    ) {
      setFocus(requested.field)
    }
  })

  const setServerError = useCallback(
    (message: string): void => {
      setError('root.serverError' as Path<Input>, { message })
    },
    [setError],
  )

  const handleClose = useCallback((): void => {
    generation.current += 1
    pendingFocus.current = undefined
    cancelConfirmation()
    reset()
    onClose()
  }, [reset, onClose, cancelConfirmation])

  const execute = useCallback(
    (approval?: { identity: Uint8Array; revision: number }): void => {
      if (prepareSubmit && running.current) return
      running.current = true
      const attempt = ++generation.current
      pendingFocus.current = undefined
      const inputRevision = revision.current
      cancelConfirmation()
      void handleSubmit(
        async (values: Output) => {
          let submittedIdentity: Uint8Array | undefined
          try {
            if (!mounted.current || attempt !== generation.current) return
            if (prepareSubmit) {
              const prepared = prepareSubmit(values)
              submittedIdentity = prepared.identity.slice()
              const confirmed =
                approval !== undefined &&
                approval.revision === revision.current &&
                approval.identity.length === prepared.identity.length &&
                approval.identity.every(
                  (byte, index) => byte === prepared.identity[index],
                )
              await prepared.send(confirmed)
            } else {
              await onSubmit(values)
            }
            if (!mounted.current || attempt !== generation.current) return
            reset()
            onClose()
            // AC-MOI-007: 保存成功時の共通経路で成功 toast を一度表示する。
            // title のみ渡し、表示時間と閉じる操作は DS の既定に従う。
            showSuccessToast({ title: t('common:toast.saved') })
          } catch (err) {
            if (!mounted.current || attempt !== generation.current) return
            if (prepareSubmit) {
              const parsed = confirmationOf(err)
              if (parsed !== undefined) {
                if (inputRevision === revision.current) {
                  if (submittedIdentity === undefined) return
                  awaiting.current = {
                    identity: submittedIdentity,
                    revision: inputRevision,
                  }
                  setConfirmation(parsed)
                }
                return
              }
            }
            if (err instanceof SilentCancelSubmitError) {
              return
            }
            if (err instanceof Error && !(err instanceof ConnectError)) {
              setServerError(err.message)
            } else if (err instanceof ConnectError && isNetworkError(err)) {
              // AC-MOI-025: 通信エラーはサーバーが返したエラー (AC-MOI-018) と文言を分ける。
              setServerError(t('common:connectError.saveFailed'))
            } else if (err instanceof ConnectError && resolveDetailedError) {
              // structured-app-error-contract.md decision D: an ErrorInfo
              // detail resolves to either a field-scoped setError (mirrors
              // the field-adjacent FieldErrorText, so the top-level alert is
              // cleared to avoid duplicate display) or an
              // operation-scoped root.serverError alert. An unresolved
              // detail (no detail attached / unrecognized reason) falls
              // back to the existing code-based message unchanged.
              const resolved = resolveDetailedError(err, t, values)
              if (resolved?.kind === 'field') {
                pendingFocus.current = {
                  field: resolved.field as Path<Input>,
                  attempt,
                }
                // shouldFocus: 長い form を下までスクロールして保存した時に、エラーの付いた欄が
                // 見えない位置にあっても、その欄へ focus (= スクロール) させる
                setError(
                  resolved.field as Path<Input>,
                  { message: resolved.message },
                  { shouldFocus: true },
                )
                clearErrors('root.serverError' as Path<Input>)
              } else if (resolved?.kind === 'operation') {
                setServerError(resolved.message)
              } else {
                setServerError(
                  mapErrorToMessage(err, t, customErrorKeys, unknownErrorKey),
                )
              }
            } else {
              setServerError(
                mapErrorToMessage(err, t, customErrorKeys, unknownErrorKey),
              )
            }
          }
        },
        (formErrors) => {
          // 項目脇に FieldErrorText が出るエラーは alert に再掲しない (重複表示になる)。
          const alertMessage = resolveFormErrorAlertMessage(
            formErrors,
            t('common:error.occurred'),
          )
          if (alertMessage !== undefined) {
            setServerError(alertMessage)
          } else {
            // 直前の失敗で出した alert が残らないよう明示的に消す
            // (項目脇の表示だけに切り替わるため)。
            clearErrors('root.serverError' as Path<Input>)
          }
        },
      )().finally(() => {
        running.current = false
      })
    },
    [
      handleSubmit,
      onSubmit,
      prepareSubmit,
      confirmationOf,
      cancelConfirmation,
      t,
      clearErrors,
      setError,
      setServerError,
      customErrorKeys,
      unknownErrorKey,
      resolveDetailedError,
      reset,
      onClose,
      showSuccessToast,
    ],
  )

  const handleSave = useCallback(() => execute(), [execute])
  const confirm = useCallback(() => {
    const approval = awaiting.current
    if (approval === undefined) return
    cancelConfirmation()
    execute(approval)
  }, [cancelConfirmation, execute])

  return {
    confirmation,
    confirm,
    cancelConfirmation,
    control,
    errors,
    serverError: errors.root?.serverError?.message,
    handleClose,
    handleSave,
    setServerError,
    reset,
    isSubmitting,
    setValue,
    watch,
    setError,
    getValues,
    isDirty,
  }
}
